import {
    Flex,
    Icon,
    VStack,
    Box,
    Input,
    Text,
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import React, { useRef } from 'react';
import { v4 } from 'uuid';
import { ToggleGroup, ToggleOption } from '../../common/components/toggle-group';
import { MapboxLocationMap } from './location-map/mapbox-location-map';
import { EsriLocationMap } from './location-map/esri-location-map';
import { LocationSelectProps } from '../types/location-select';
import { LocationSelectValueDisplay } from './location-select-value-display';
import { LocationTable } from './location-table/location-table';
import { LocationList } from './location-list/location-list';
import { useLocationSelectState } from './use-location-select-state';
import { LocationSelectHint } from './common/location-select-hint';
import { getLocationSummary } from '../helpers/location-summary-v2';
import { Tooltip } from '../../common/components/tooltip';
import { getOneSizeSmaller } from '../../common/helpers/utils';
import { logDebug } from '../../monitoring/log';

const toggleOptions: ToggleOption[] = [
    {
        id: 'COMPACT',
        label: 'COMPACT',
        icon: 'BulletedList',
        testId: 'LOCATION_MODE_COMPACT',
        tooltip: 'Condensed List',
    },
    {
        id: 'LIST',
        label: 'LIST',
        icon: 'LayoutThreeColumns',
        testId: 'LOCATION_MODE_LIST',
        tooltip: 'Expanded List',
    },
    { id: 'MAP', label: 'MAP', icon: 'Map', testId: 'LOCATION_MODE_MAP', tooltip: 'Map' },
];

export const LocationSelect = observer(
    function LocationSelect(props: LocationSelectProps, ref): React.ReactElement {
        const {
            autoFocus,
            isDisabled,
            moveFocusNext,
            onBlur,
            onChange,
            placeholder,
            popoverRef,
            value,
            placeInputOutside,
            hideLocationSummaryAdditionalLines,
            cautionProps,
            hasFormErrors,
            ...inputProps
        } = props;

        const {
            closePopover,
            composedRef,
            currentMode,
            inputRef,
            isMapDisabled,
            isPopoverOpen,
            locationDisplayProps,
            onInputChange,
            onToggle,
            openPopover,
            query,
            useMapboxMapForLocationInputs,
            wrapperRef,
        } = useLocationSelectState({ ...props, ref });

        const size = locationDisplayProps.size;
        const smallerSize = getOneSizeSmaller(size);
        const isLgSize = size === 'lg';
        const activeResultsMode = currentMode === 'LIST' || currentMode === 'COMPACT';
        const instanceIdRef = useRef(v4());
        const instanceId = instanceIdRef.current;
        const optionIdPrefix = `location-select-option-${instanceId}`;
        const ariaControlsId =
            currentMode === 'LIST'
                ? `location-select-results-listbox-${instanceId}`
                : currentMode === 'COMPACT'
                  ? `location-select-compact-results-listbox-${instanceId}`
                  : undefined;
        const highlightedItem =
            locationDisplayProps.store.highlightedLocationIndex !== null
                ? locationDisplayProps.store.renderedLocationData.renderedList[
                      locationDisplayProps.store.highlightedLocationIndex
                  ]
                : undefined;
        const activeDescendantId =
            isPopoverOpen &&
            activeResultsMode &&
            highlightedItem !== undefined
                ? `${optionIdPrefix}-${highlightedItem.id}`
                : undefined;

        const triggerRef = useRef<HTMLDivElement>(null);
        const noResults = locationDisplayProps.store.data.list.length === 0;
        let locationSummarySecondLineText = '';
        let locationSummaryThirdLineText = '';
        if (!hideLocationSummaryAdditionalLines) {
            const locationSummary = getLocationSummary(value);
            locationSummarySecondLineText = locationSummary.secondLine;
            locationSummaryThirdLineText = locationSummary.thirdLine;
        }

        return (
            <Box ref={wrapperRef} w="100%">
                <Popover isOpen={isPopoverOpen} hasArrow={false}>
                    <VStack spacing={0} alignItems="flex-start" className="location-input-wrapper">
                        <PopoverAnchor asChild>
                            <Box w="100%">
                                {placeInputOutside ? (
                                    <Input
                                        leftIcon="Search"
                                        aria-label={inputProps['aria-label']}
                                        role="combobox"
                                        aria-autocomplete="list"
                                        aria-expanded={isPopoverOpen && activeResultsMode}
                                        aria-controls={
                                            isPopoverOpen && activeResultsMode
                                                ? ariaControlsId
                                                : undefined
                                        }
                                        aria-activedescendant={activeDescendantId}
                                        // @ts-expect-error arc complains about the ref type but it works
                                        ref={composedRef}
                                        isDisabled={isDisabled}
                                        autoFocus={autoFocus}
                                        value={query}
                                        data-testid="location-select-input"
                                        onClick={() => {
                                            if (!isPopoverOpen) {
                                                openPopover();
                                            }
                                        }}
                                        onChange={onInputChange}
                                        onBlur={onBlur}
                                        placeholder={placeholder}
                                        onFocus={() => {
                                            logDebug(
                                                '[FOCUS DEBUG] location-select onFocus (placeInputOutside) scheduling inputRef.select()'
                                            );
                                            setTimeout(() => {
                                                inputRef?.current?.select?.();
                                            });
                                        }}
                                        rightAddon={
                                            value?.areCoordinatesVerified ? (
                                                <Tooltip
                                                    overlay={
                                                        <Text fontSize={smallerSize}>VERIFIED</Text>
                                                    }
                                                >
                                                    <Icon
                                                        icon="VerifiedLocation"
                                                        color="positive.default"
                                                        data-testid="location-select-verified"
                                                        style={{ zIndex: 1 }}
                                                    />
                                                </Tooltip>
                                            ) : value?.source === 'FORCED_LOCATION' &&
                                              //@ts-expect-error
                                              value?.isForcedSubpremiseResult !== undefined ? (
                                                <Text>FORCED</Text>
                                            ) : undefined
                                        }
                                    />
                                ) : (
                                    <LocationSelectValueDisplay
                                        onClick={() => {
                                            if (!isPopoverOpen) {
                                                openPopover();
                                            }
                                        }}
                                        onDelete={() => {
                                            onChange?.(null);
                                        }}
                                        value={value}
                                        cautionProps={cautionProps}
                                        hasFormErrors={hasFormErrors}
                                        locationSelectTriggerRef={triggerRef}
                                        isDisabled={isDisabled}
                                        size={size}
                                    />
                                )}
                            </Box>
                        </PopoverAnchor>
                        {placeInputOutside && (
                            <>
                                {locationSummarySecondLineText ? (
                                    <Text fontSize={smallerSize}>
                                        {locationSummarySecondLineText}
                                    </Text>
                                ) : undefined}
                                {locationSummaryThirdLineText ? (
                                    <Text fontSize={smallerSize} color="tertiary">
                                        {locationSummaryThirdLineText}
                                    </Text>
                                ) : undefined}
                            </>
                        )}
                    </VStack>
                    <PopoverContent
                        align="start"
                        side="bottom"
                        hasPadding={false}
                        onInteractOutside={(e) => {
                            if (
                                e.target !== inputRef.current &&
                                e.target &&
                                !triggerRef.current?.contains?.(e.target as Node)
                            ) {
                                closePopover();
                                // @ts-expect-error there's a typeerror about the type of event
                                // here but it works
                                onBlur?.(e);
                            }
                        }}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        ref={popoverRef}
                        onKeyDown={(e) => {
                            wrapperRef.current?.onkeydown?.(e as unknown as KeyboardEvent);
                        }}
                    >
                        <Box
                            style={{
                                ...(currentMode === 'COMPACT'
                                    ? { width: isLgSize ? '500px' : '420px' }
                                    : {
                                          minWidth: isLgSize ? '730px' : '650px',
                                          maxWidth: '850px',
                                      }),
                            }}
                        >
                            <Flex
                                paddingY={3}
                                pl={3}
                                pr={3}
                                justifyContent={placeInputOutside ? 'flex-end' : 'space-between'}
                                alignContent="center"
                            >
                                {!placeInputOutside && (
                                    <Input
                                        leftIcon="Search"
                                        aria-label={inputProps['aria-label']}
                                        role="combobox"
                                        aria-autocomplete="list"
                                        aria-expanded={isPopoverOpen && activeResultsMode}
                                        aria-controls={
                                            isPopoverOpen && activeResultsMode
                                                ? ariaControlsId
                                                : undefined
                                        }
                                        aria-activedescendant={activeDescendantId}
                                        // @ts-expect-error arc complains about the ref type but it works
                                        ref={composedRef}
                                        isDisabled={isDisabled}
                                        autoFocus
                                        value={query}
                                        data-testid="location-select-input"
                                        onChange={onInputChange}
                                        onBlur={onBlur}
                                        placeholder={placeholder}
                                        onFocus={() => {
                                            logDebug(
                                                '[FOCUS DEBUG] location-select onFocus (inside popover) scheduling inputRef.select()'
                                            );
                                            setTimeout(() => {
                                                inputRef?.current?.select?.();
                                            });
                                        }}
                                    />
                                )}

                                <Box pl={3}>
                                    <ToggleGroup
                                        disabledOptions={isMapDisabled ? ['MAP'] : []}
                                        onItemChanged={onToggle}
                                        options={toggleOptions}
                                        value={toggleOptions.find((t) => t.id === currentMode)}
                                        useNewStyles
                                    />
                                </Box>
                            </Flex>
                            <hr
                                style={{
                                    borderTop: '1px solid var(--arc-colors-border-default)',
                                    width: '100%',
                                }}
                            />
                            <Box h={noResults ? 'fit-content' : '300px'}>
                                {noResults ? (
                                    <LocationSelectHint size={smallerSize} />
                                ) : currentMode === 'LIST' ? (
                                    <LocationTable
                                        {...locationDisplayProps}
                                        listBoxId={ariaControlsId}
                                        optionIdPrefix={optionIdPrefix}
                                    />
                                ) : currentMode === 'COMPACT' ? (
                                    <LocationList
                                        {...locationDisplayProps}
                                        listBoxId={ariaControlsId}
                                        optionIdPrefix={optionIdPrefix}
                                    />
                                ) : useMapboxMapForLocationInputs ? (
                                    <MapboxLocationMap {...locationDisplayProps} />
                                ) : (
                                    <EsriLocationMap {...locationDisplayProps} surface="location-select" />
                                )}
                            </Box>
                        </Box>
                    </PopoverContent>
                </Popover>
            </Box>
        );
    },
    {
        forwardRef: true,
    }
);
